import { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'
import { ProductCarouselSlide } from 'shared-types'
import { Image } from '~/components/Image'
import { convertProductImageUrl, getSlideSrcSet } from '~/helpers'
import { usePage } from '~/hooks'
import styles from './GalleryImage.module.scss'

export interface GalleryImageProps {
  slide: ProductCarouselSlide
  priority?: boolean
  onClick?: () => void
  zoomable?: boolean
  // Candidate widths requested from the CDN, and the sizes hint the browser
  // picks between them with. See DEFAULT_SIZES for the defaults.
  srcSetWidths?: number[]
  sizes?: string
  // Reports whether the image is currently zoomed in, so the container can
  // hold off gestures of its own, such as swiping between slides.
  onZoomChange?: (isZoomed: boolean) => void
}

// The image covers the PDP gallery column at the viewer's device pixel ratio.
// The column widths mirror the breakpoints of .pdpGrid in
// ProductDashboard.module.css; below 1024px the gallery spans the full
// viewport width. The modal shares the same set, so opening it reuses the
// candidate the page already fetched instead of loading a larger one. A
// zoomed image is upscaled rather than fetched larger, as a trade-off against
// loading several times the pixels for every image opened in the lightbox.
export const DEFAULT_SRCSET_WIDTHS = [400, 640, 960, 1280, 1600, 1920]
export const DEFAULT_SIZES =
  '(min-width: 1600px) 900px, (min-width: 1440px) 864px, (min-width: 1380px) 60vw, (min-width: 1024px) 55vw, 100vw'
const ZOOM_STEP = 0.4
const CLICK_ZOOM_SCALE = 2.5
const MIN_SCALE = 1
const MAX_SCALE = 5
const DRAG_THRESHOLD = 3

type Transform = { scale: number; tx: number; ty: number }
type DragState = {
  pointerX: number
  pointerY: number
  startTx: number
  startTy: number
  moved: boolean
}
type PinchState = {
  startDistance: number
  startScale: number
  startTx: number
  startTy: number
  startMidX: number
  startMidY: number
}

const INITIAL_TRANSFORM: Transform = { scale: MIN_SCALE, tx: 0, ty: 0 }

const clampScale = (value: number) => {
  return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value))
}

const clampPan = (transform: Transform, rect: DOMRect): Transform => {
  const minTx = (1 - transform.scale) * rect.width
  const minTy = (1 - transform.scale) * rect.height

  return {
    scale: transform.scale,
    tx: Math.max(minTx, Math.min(0, transform.tx)),
    ty: Math.max(minTy, Math.min(0, transform.ty)),
  }
}

const zoomAroundPoint = (
  prev: Transform,
  nextScale: number,
  pointerClientX: number,
  pointerClientY: number,
  rect: DOMRect
): Transform => {
  if (nextScale <= MIN_SCALE) {
    return INITIAL_TRANSFORM
  }

  const px = pointerClientX - rect.left
  const py = pointerClientY - rect.top
  const ratio = nextScale / prev.scale
  const tx = px - ratio * (px - prev.tx)
  const ty = py - ratio * (py - prev.ty)

  return clampPan({ scale: nextScale, tx, ty }, rect)
}

const applyPanDelta = (
  prev: Transform,
  drag: DragState,
  dx: number,
  dy: number,
  rect: DOMRect
): Transform => {
  return clampPan(
    { scale: prev.scale, tx: drag.startTx + dx, ty: drag.startTy + dy },
    rect
  )
}

const getTouchDistance = (touches: TouchList) => {
  const a = touches.item(0)
  const b = touches.item(1)

  if (!a || !b) {
    return 0
  }

  return Math.hypot(b.clientX - a.clientX, b.clientY - a.clientY)
}

const getTouchMidpoint = (touches: TouchList) => {
  const a = touches.item(0)
  const b = touches.item(1)

  if (!a || !b) {
    return { x: 0, y: 0 }
  }

  return { x: (a.clientX + b.clientX) / 2, y: (a.clientY + b.clientY) / 2 }
}

const computePinchTransform = (
  pinch: PinchState,
  touches: TouchList,
  distance: number,
  rect: DOMRect
): Transform => {
  const mid = getTouchMidpoint(touches)
  const ratio = distance / pinch.startDistance
  const nextScale = clampScale(pinch.startScale * ratio)
  const startMidLocalX = pinch.startMidX - rect.left
  const startMidLocalY = pinch.startMidY - rect.top
  const imgPointX = (startMidLocalX - pinch.startTx) / pinch.startScale
  const imgPointY = (startMidLocalY - pinch.startTy) / pinch.startScale
  const tx = mid.x - rect.left - nextScale * imgPointX
  const ty = mid.y - rect.top - nextScale * imgPointY

  return clampPan({ scale: nextScale, tx, ty }, rect)
}

export const GalleryImage = ({
  slide,
  priority = false,
  onClick,
  zoomable = false,
  srcSetWidths = DEFAULT_SRCSET_WIDTHS,
  sizes = DEFAULT_SIZES,
  onZoomChange,
}: GalleryImageProps) => {
  const { siteCode } = usePage()
  const src = convertProductImageUrl(slide.src, siteCode)
  const rootRef = useRef<HTMLDivElement>(null)
  const transformRef = useRef<Transform>(INITIAL_TRANSFORM)
  const dragRef = useRef<DragState | null>(null)
  const pinchRef = useRef<PinchState | null>(null)
  const [transform, setTransform] = useState<Transform>(INITIAL_TRANSFORM)
  const [isInteracting, setIsInteracting] = useState(false)

  transformRef.current = transform

  const srcSet = getSlideSrcSet(src, srcSetWidths)

  useEffect(() => {
    setTransform(INITIAL_TRANSFORM)
  }, [slide.src])

  const updateDragWithPointer = (clientX: number, clientY: number) => {
    const drag = dragRef.current

    if (!drag) {
      return
    }

    const dx = clientX - drag.pointerX
    const dy = clientY - drag.pointerY

    if (Math.abs(dx) > DRAG_THRESHOLD || Math.abs(dy) > DRAG_THRESHOLD) {
      drag.moved = true
    }

    const rect = rootRef.current?.getBoundingClientRect()

    if (!rect) {
      return
    }

    setTransform((prev) => {
      return applyPanDelta(prev, drag, dx, dy, rect)
    })
  }

  const handlePinchMove = (event: TouchEvent, rect: DOMRect) => {
    const pinch = pinchRef.current

    if (!pinch) {
      return
    }

    event.preventDefault()
    const distance = getTouchDistance(event.touches)

    if (!distance) {
      return
    }

    setTransform(computePinchTransform(pinch, event.touches, distance, rect))
  }

  const handleTouchDragMove = (event: TouchEvent) => {
    if (!dragRef.current) {
      return
    }

    const touch = event.touches.item(0)

    if (!touch) {
      return
    }

    event.preventDefault()
    updateDragWithPointer(touch.clientX, touch.clientY)
  }

  useEffect(() => {
    if (!zoomable) {
      return undefined
    }

    const element = rootRef.current

    if (!element) {
      return undefined
    }

    const handleWheel = (event: WheelEvent) => {
      event.preventDefault()
      const direction = event.deltaY < 0 ? 1 : -1
      const magnitude = Math.min(1, Math.abs(event.deltaY) / 100)
      const delta = direction * ZOOM_STEP * magnitude
      const rect = element.getBoundingClientRect()

      setTransform((prev) => {
        const next = clampScale(prev.scale + delta)

        if (next === prev.scale) {
          return prev
        }

        return zoomAroundPoint(prev, next, event.clientX, event.clientY, rect)
      })
    }

    const handleTouchMove = (event: TouchEvent) => {
      const rect = element.getBoundingClientRect()

      if (event.touches.length === 2) {
        handlePinchMove(event, rect)
      } else if (event.touches.length === 1) {
        handleTouchDragMove(event)
      }
    }

    element.addEventListener('wheel', handleWheel, { passive: false })
    element.addEventListener('touchmove', handleTouchMove, { passive: false })

    return () => {
      element.removeEventListener('wheel', handleWheel)
      element.removeEventListener('touchmove', handleTouchMove)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zoomable])

  const beginPinch = (touches: TouchList) => {
    const mid = getTouchMidpoint(touches)
    pinchRef.current = {
      startDistance: getTouchDistance(touches),
      startScale: transformRef.current.scale,
      startTx: transformRef.current.tx,
      startTy: transformRef.current.ty,
      startMidX: mid.x,
      startMidY: mid.y,
    }
    dragRef.current = null
    setIsInteracting(true)
  }

  const beginDrag = (clientX: number, clientY: number) => {
    dragRef.current = {
      pointerX: clientX,
      pointerY: clientY,
      startTx: transformRef.current.tx,
      startTy: transformRef.current.ty,
      moved: false,
    }
    setIsInteracting(true)
  }

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      beginPinch(event.nativeEvent.touches)

      return
    }

    const canDrag = transformRef.current.scale > MIN_SCALE

    if (event.touches.length === 1 && canDrag) {
      const touch = event.touches.item(0)

      if (touch) {
        beginDrag(touch.clientX, touch.clientY)
      }
    }
  }

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 0) {
      pinchRef.current = null
      dragRef.current = null
      setIsInteracting(false)

      return
    }

    if (event.touches.length === 1) {
      pinchRef.current = null
      const touch = event.touches.item(0)

      if (touch && transformRef.current.scale > MIN_SCALE) {
        beginDrag(touch.clientX, touch.clientY)
      }
    }
  }

  const handleMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!zoomable || transformRef.current.scale <= MIN_SCALE) {
      return
    }

    event.preventDefault()
    beginDrag(event.clientX, event.clientY)

    const handleMouseMove = (e: MouseEvent) => {
      updateDragWithPointer(e.clientX, e.clientY)
    }

    const handleMouseUp = () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('mouseup', handleMouseUp)
      setIsInteracting(false)
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseup', handleMouseUp)
  }

  const toggleClickZoom = (pointerClientX: number, pointerClientY: number) => {
    const rect = rootRef.current?.getBoundingClientRect()

    if (!rect) {
      return
    }

    setTransform((prev) => {
      if (prev.scale > MIN_SCALE) {
        return INITIAL_TRANSFORM
      }

      return zoomAroundPoint(
        prev,
        CLICK_ZOOM_SCALE,
        pointerClientX,
        pointerClientY,
        rect
      )
    })
  }

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (dragRef.current?.moved) {
      dragRef.current = null

      return
    }

    dragRef.current = null

    if (zoomable) {
      toggleClickZoom(event.clientX, event.clientY)

      return
    }

    onClick?.()
  }

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return
    }

    event.preventDefault()

    if (zoomable) {
      const rect = rootRef.current?.getBoundingClientRect()

      if (!rect) {
        return
      }

      toggleClickZoom(rect.left + rect.width / 2, rect.top + rect.height / 2)
    } else {
      onClick?.()
    }
  }

  const isInteractive = zoomable || !!onClick
  const isZoomed = zoomable && transform.scale > MIN_SCALE

  useEffect(() => {
    onZoomChange?.(isZoomed)
  }, [isZoomed, onZoomChange])

  return (
    <div
      ref={rootRef}
      className={classNames(styles.root, {
        [styles.clickable]: !!onClick && !zoomable,
        [styles.zoomable]: zoomable,
        [styles.zoomed]: isZoomed,
        [styles.dragging]: zoomable && isInteracting,
      })}
      onClick={isInteractive ? handleClick : undefined}
      onMouseDown={zoomable ? handleMouseDown : undefined}
      onTouchStart={zoomable ? handleTouchStart : undefined}
      onTouchEnd={zoomable ? handleTouchEnd : undefined}
      role={isInteractive ? 'button' : undefined}
      tabIndex={isInteractive ? 0 : undefined}
      onKeyDown={isInteractive ? handleKeyDown : undefined}>
      <Image
        className={styles.image}
        src={src}
        alt={slide.altText}
        width={slide.width}
        height={slide.height}
        priority={priority}
        addSrcSet={false}
        srcSet={srcSet}
        sizes={sizes}
        style={
          zoomable
            ? {
                transform: `translate(${transform.tx}px, ${transform.ty}px) scale(${transform.scale})`,
                transformOrigin: '0 0',
                transition: isInteracting ? 'none' : 'transform 0.15s ease-out',
              }
            : undefined
        }
      />
    </div>
  )
}
